/**
 * The example entity, end to end. `/feature` copies this shape for every new
 * noun in an app. Pure: no React, no router, no vendor SDK — CLAUDE.md
 * "Business logic lives in src/core/**".
 */
import { z } from 'zod'
import { patchOf } from '../shared/patch.ts'

export const itemStatuses = ['todo', 'in_progress', 'done', 'archived'] as const
export type ItemStatus = (typeof itemStatuses)[number]

export const itemSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1, 'Give it a title.').max(120),
  description: z.string().max(2000).default(''),
  status: z.enum(itemStatuses),
  createdAt: z.iso.datetime(),
  /**
   * When a real value last changed — kept by the database
   * (`keep_record_timestamps()`, docs/DATA-MODEL.md → "Main record types"), so
   * saving it unchanged doesn't move it. Equal to `createdAt` until then.
   */
  updatedAt: z.iso.datetime(),
  /**
   * The person who created it. Null for records created before this existed,
   * or whose creator's account has since been deleted — the record outlives
   * the person, because it belongs to the workspace.
   */
  createdBy: z.object({ id: z.string(), name: z.string() }).nullable().default(null),
  /**
   * When it was deleted, or null. Deleting never destroys a record: it moves to
   * Recently deleted, where it can be restored, and only "Delete forever" from
   * there removes it. Every app table follows this — see CLAUDE.md.
   */
  deletedAt: z.iso.datetime().nullable().default(null),
})

export type Item = z.infer<typeof itemSchema>

export const createItemSchema = itemSchema.pick({ title: true, description: true }).extend({
  status: z.enum(itemStatuses).default('todo'),
})

export type CreateItemInput = z.infer<typeof createItemSchema>

export const updateItemSchema = patchOf(
  itemSchema.pick({ title: true, description: true, status: true }),
)

export type UpdateItemInput = z.infer<typeof updateItemSchema>

/** Statuses a user can move an item to, in the order the status listbox shows. */
export const selectableStatuses: readonly ItemStatus[] = ['todo', 'in_progress', 'done']

export const statusLabels: Record<ItemStatus, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
  archived: 'Archived',
}

export function isOpen(item: Item): boolean {
  return item.status === 'todo' || item.status === 'in_progress'
}

/** Newest first — the order every list in the app shows items in. */
export function sortByNewest(items: readonly Item[]): Item[] {
  return [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export function countByStatus(items: readonly Item[]): Record<ItemStatus, number> {
  const counts: Record<ItemStatus, number> = {
    todo: 0,
    in_progress: 0,
    done: 0,
    archived: 0,
  }
  for (const item of items) counts[item.status] += 1
  return counts
}
